// ── RequestRenewalModal — SHARED multi-field renewal request modal ─────────────
//
// ORA.5 CRITICAL-2: extracted to shared/ from risk-acceptances/ (3rd cluster-
// canonical UI-layer SHARED extraction). mutationHook prop injected by caller:
//   vendor RA: mutationHook={useRequestRenewal}
//   ORA:       mutationHook={useRequestOrgRenewal as any}
//
// Parallel to RiskAcceptanceActionModal.tsx — same shared/ extraction pattern.
// entry typed as minimal interface; callers pass their domain-specific entry.
//
// Submit gate, field order, character limits unchanged from RA.5 original.

import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// ── Props ─────────────────────────────────────────────────────────────────────

interface RenewalEntry {
  id: string;
  title: string | null;
  acceptance_expires_at: string | null;
}

// mutationHook: no-arg hook factory; returns mutation object.
// Typed loosely (unknown return) to accept both vendor and ORA hooks without
// TypeScript contravariance errors at the call site.
type RenewalMutationHook = () => {
  mutateAsync: (args: {
    parentId: string;
    payload: { renewal_justification: string; new_acceptance_expires_at: string };
  }) => Promise<unknown>;
  isPending: boolean;
};

export interface RequestRenewalModalProps {
  entry: RenewalEntry;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSuccess: (childEntry: any) => void;
  onClose: () => void;
  mutationHook: RenewalMutationHook;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function RequestRenewalModal({
  entry,
  onSuccess,
  onClose,
  mutationHook,
}: RequestRenewalModalProps) {
  const [justification, setJustification] = useState('');
  const [newExpiryDate, setNewExpiryDate] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [inModalError, setInModalError] = useState('');

  const renewalMutation = mutationHook();

  // Submit gate — all 4 conditions must hold (spec v2 §3.5)
  const todayStr = new Date().toISOString().split('T')[0];
  const trimmed = justification.trim();
  const canSubmit =
    trimmed.length >= 50 &&
    trimmed.length <= 2000 &&
    newExpiryDate !== '' &&
    newExpiryDate > todayStr &&
    (entry.acceptance_expires_at == null || newExpiryDate > entry.acceptance_expires_at);

  async function handleSubmit() {
    setFieldErrors({});
    setInModalError('');

    try {
      const child = await renewalMutation.mutateAsync({
        parentId: entry.id,
        payload: {
          renewal_justification:     trimmed,
          new_acceptance_expires_at: newExpiryDate,
        },
      });
      onSuccess(child);
      onClose();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { errors?: unknown } } };
      const status = axiosErr?.response?.status;
      const errData = axiosErr?.response?.data?.errors;

      if (status === 400) {
        if (errData && !Array.isArray(errData) && typeof errData === 'object') {
          const fe = errData as Record<string, string[]>;
          const mapped: Record<string, string> = {};
          for (const [key, msgs] of Object.entries(fe)) {
            mapped[key] = Array.isArray(msgs) ? msgs[0] ?? 'Validation error' : String(msgs);
          }
          setFieldErrors(mapped);
        } else if (Array.isArray(errData)) {
          toast.error((errData as Array<{ message: string }>)[0]?.message ?? 'Validation error');
        } else {
          toast.error('Validation error');
        }
      } else if (status === 409) {
        // IllegalTransition — window may have closed or parent state changed
        setInModalError(
          'Cannot request renewal. The parent entry may no longer be eligible ' +
          '(wrong state or renewal window closed). Please refresh and try again.',
        );
      } else if (status === 403) {
        toast.error("You don't have permission for this action");
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    }
  }

  const justificationCount = trimmed.length;
  const justificationHint =
    justificationCount < 50
      ? `at least 50 characters (${justificationCount}/50)`
      : `${justificationCount}/2000`;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Request Renewal</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Entry title context */}
          <div className="text-sm text-text-secondary">
            Requesting renewal for:{' '}
            <span className="font-medium text-text-primary">{entry.title}</span>
          </div>

          {/* In-modal error (409 / window-closed) */}
          {inModalError && (
            <div className="rounded-md bg-accent-rose-dim px-3 py-2 text-sm text-accent-rose">
              {inModalError}
            </div>
          )}

          {/* Field 1: Renewal justification (textarea) — first per spec v2 §3.5 */}
          <div className="space-y-1.5">
            <Label htmlFor="renewal-justification">Renewal justification</Label>
            <Textarea
              id="renewal-justification"
              value={justification}
              onChange={(e) => { setJustification(e.target.value); setFieldErrors({}); }}
              rows={5}
              maxLength={2000}
              placeholder="Explain why this risk response should be renewed (50–2000 characters)"
              className="resize-none"
            />
            {fieldErrors.renewal_justification ? (
              <p className="text-xs text-accent-rose">{fieldErrors.renewal_justification}</p>
            ) : (
              <p className="text-xs text-text-secondary">{justificationHint}</p>
            )}
          </div>

          {/* Field 2: New expiry date (native <Input type="date">) — second per spec v2 §3.5 */}
          <div className="space-y-1.5">
            <Label htmlFor="new-expiry-date">Proposed new expiry date</Label>
            <Input
              id="new-expiry-date"
              type="date"
              value={newExpiryDate}
              onChange={(e) => { setNewExpiryDate(e.target.value); setFieldErrors({}); }}
              min={
                entry.acceptance_expires_at
                  ? entry.acceptance_expires_at
                  : todayStr
              }
            />
            {fieldErrors.new_acceptance_expires_at ? (
              <p className="text-xs text-accent-rose">{fieldErrors.new_acceptance_expires_at}</p>
            ) : (
              <p className="text-xs text-text-secondary">
                Must be after current expiry
                {entry.acceptance_expires_at ? ` (${entry.acceptance_expires_at})` : ''}.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={renewalMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || renewalMutation.isPending}
          >
            {renewalMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Request Renewal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
